import { z } from 'zod';
import { ROLES } from '../catalogos/acceso.js';
import { MODULOS, PAQUETES_COMERCIALES } from '../catalogos/modulos.js';
import { esquemaMotivo, esquemaZonaHoraria } from './comunes.js';
import { esquemaSlug } from './organizacion.js';
import { esquemaContraseña } from './usuario.js';
/**
 * Plano de plataforma (ADR-001).
 *
 * Es otro dominio, no un rol mas: sus cuentas no pertenecen a ninguna
 * organizacion y sus sesiones no entran a las rutas clinicas. Ningun rol de
 * plataforma lee contenido clinico por serlo.
 */
export const ROLES_DE_PLATAFORMA = ['administrador', 'soporte', 'finanzas'];
/** Codigo TOTP de seis digitos. MFA es obligatorio en plataforma (T-03). */
export const esquemaCodigoTotp = z
    .string()
    .trim()
    .regex(/^\d{6}$/, 'El código tiene seis dígitos');
export const esquemaIngresoPlataforma = z.object({
    correo: z.string().trim().toLowerCase().email(),
    contrasena: z.string().min(1, 'Escriba su contraseña'),
    codigo: esquemaCodigoTotp,
});
/**
 * Alta guiada de un consultorio desde la consola (plan maestro §11.2).
 *
 * A diferencia del registro autoservicio, aqui no viaja ninguna contrasena:
 * el propietario recibe una invitacion de un solo uso y la fija el mismo.
 */
export const esquemaAltaOrganizacion = z.object({
    organizacion: z.object({
        nombre: z.string().trim().min(2).max(120),
        slug: esquemaSlug,
        zonaHoraria: esquemaZonaHoraria,
    }),
    propietario: z.object({
        nombre: z.string().trim().min(2).max(120),
        correo: z.string().trim().toLowerCase().email(),
    }),
    paquete: z.enum(PAQUETES_COMERCIALES),
    /** Modulos adicionales al paquete (especialidades, extras). */
    modulosAdicionales: z.array(z.enum(MODULOS)).max(MODULOS.length).default([]),
});
/** Invitacion de una persona del consultorio (propietario o profesional). */
export const esquemaInvitacion = z.object({
    nombre: z.string().trim().min(2).max(120),
    correo: z.string().trim().toLowerCase().email(),
    rol: z.enum(ROLES),
});
/**
 * Aceptar una invitacion: la persona fija SU contrasena. Nadie mas la conoce,
 * asi que no hay cambio obligatorio despues (a diferencia de la provisional).
 */
export const esquemaAceptarInvitacion = z.object({
    token: z.string().trim().min(20).max(200),
    contrasena: esquemaContraseña,
    cedulaProfesional: z.string().trim().max(30).default(''),
    especialidad: z.string().trim().max(120).default(''),
});
/** Suspender o reactivar a una persona desde la plataforma: siempre con motivo. */
export const esquemaEstadoDeUsuarioPlataforma = z
    .object({ activo: z.boolean(), motivo: esquemaMotivo })
    .strict();
/**
 * Alta de un medico en un solo paso. Con `consultorio.tipo = 'nuevo'` se crea
 * su consultorio con el modulo de su especialidad y el medico queda de
 * propietario; con `'existente'` se le invita a ese consultorio y, si le falta,
 * se le concede el modulo de la especialidad. Nunca viaja una contrasena: el
 * medico elige la suya al aceptar la invitacion.
 */
export const esquemaAltaDeMedico = z
    .object({
    nombre: z.string().trim().min(2).max(120),
    correo: z.string().trim().toLowerCase().email(),
    /** Modulo de especialidad; `null` = sin modulo de especialidad. */
    especialidad: z.enum(MODULOS).nullable(),
    consultorio: z.discriminatedUnion('tipo', [
        z
            .object({
            tipo: z.literal('nuevo'),
            nombre: z.string().trim().min(2).max(120),
            slug: esquemaSlug,
            zonaHoraria: esquemaZonaHoraria,
            paquete: z.enum(PAQUETES_COMERCIALES),
            /** Activar el consultorio en cuanto el medico acepte la invitacion. */
            activarAlAceptar: z.boolean().default(true),
        })
            .strict(),
        z
            .object({
            tipo: z.literal('existente'),
            organizacionId: z.string().regex(/^[a-f0-9]{24}$/i, 'Consultorio inválido'),
            rol: z.enum(['medico', 'propietario']).default('medico'),
        })
            .strict(),
    ]),
})
    .strict();
//# sourceMappingURL=plataforma.js.map